import {
  type DeliveryChargeFulfillmentType,
  type DeliveryMethodContainerRulesContainerTypesType,
  type FulfillmentTypeCreateRequestType,
} from "@sellar-io/api-v2/types";
import { useId, useState } from "react";
import { TableSimple, H5, InputCheckbox } from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { SpinnerLegacy } from "../../../components/spinner";

import {
  buildDeliveryMethodCreateRequest,
  getDeliveryMethodCreateContainerKey,
} from "./deliveryMethodCreateRequest";

const deliveryMethodTypes: Array<{
  label: string;
  type: DeliveryChargeFulfillmentType;
}> = [
  { label: "Direct", type: "direct" },
  { label: "Courier", type: "courier" },
  { label: "Pallet", type: "haulier" },
];

export const DeliveryMethodCreateForm = ({
  formId,
  availableContainers,
  isLoading,
  error,
  onSubmit,
}: {
  formId?: string;
  availableContainers: DeliveryMethodContainerRulesContainerTypesType;
  isLoading: boolean;
  error?: string;
  onSubmit: (request: FulfillmentTypeCreateRequestType) => void;
}) => {
  const nameInputId = useId();
  const [name, setName] = useState("");
  const [selectedType, setSelectedType] =
    useState<DeliveryChargeFulfillmentType>("direct");
  const [excludedContainerKeys, setExcludedContainerKeys] = useState<
    Set<string>
  >(new Set());

  return (
    <form
      id={formId}
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();

        onSubmit(
          buildDeliveryMethodCreateRequest({
            name,
            fulfillmentType: selectedType,
            availableContainers,
            excludedContainerKeys,
          }),
        );
      }}
    >
      <div className="flex flex-col gap-2">
        <label htmlFor={nameInputId}>
          <H5>Name</H5>
        </label>
        <InputContainer>
          <InputText
            id={nameInputId}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </InputContainer>
      </div>
      {error ? <Alert type="error" message={error} /> : null}
      <div className="flex flex-col gap-2">
        <InputLabel>Select type</InputLabel>
        <RadioGroup
          value={selectedType}
          onChange={(value) =>
            setSelectedType(value as DeliveryChargeFulfillmentType)
          }
        >
          <RadioGroupContainer>
            {deliveryMethodTypes.map((type) => (
              <RadioGroupOption key={type.type} value={type.type}>
                {type.label}
              </RadioGroupOption>
            ))}
          </RadioGroupContainer>
        </RadioGroup>
      </div>

      {selectedType === "direct" ? (
        <>
          <Alert
            type="information"
            message="You will only be able to assign this delivery method to customers
              that fall into your postcode-defined zones"
          />
          <Alert
            type="success"
            message={
              <>
                Your <b>direct</b> delivery schedule(s) will be referenced for
                this delivery method
              </>
            }
            className="w-full"
          />
        </>
      ) : null}

      {selectedType === "courier" ? (
        <Alert
          type="success"
          message={
            <>
              Your <b>courier</b> delivery schedule will be referenced for this
              delivery method
            </>
          }
          className="w-full"
        />
      ) : null}

      <div className="flex flex-col gap-2">
        <H5>Available containers</H5>
        <p className="text-sm text-zinc-500">
          Select which containers can be fulfilled by this delivery method.
        </p>
        {isLoading ? (
          <div className="flex justify-center p-4">
            <SpinnerLegacy />
          </div>
        ) : (
          <TableSimple
            roundedTop={true}
            columns={[
              {
                id: "container",
                header: "Containers",
                cell: ({ row }) => (
                  <span>{`${row.original.volume}${row.original.displayUnit} ${row.original.displayName}`}</span>
                ),
              },
              {
                id: "availability",
                header: "Available?",
                headerClassName: "justify-center",
                cellClassName: "justify-center",
                cell: ({ row }) => {
                  const containerKey = getDeliveryMethodCreateContainerKey(
                    row.original,
                  );
                  const selected = !excludedContainerKeys.has(containerKey);

                  return (
                    <label
                      className="flex cursor-pointer justify-center"
                      data-interactive="true"
                    >
                      <InputCheckbox
                        aria-label={`${row.original.volume}${row.original.displayUnit} ${row.original.displayName}`}
                        required={false}
                        checked={selected}
                        onChange={() => {
                          setExcludedContainerKeys((currentKeys) => {
                            const nextKeys = new Set(currentKeys);

                            if (nextKeys.has(containerKey)) {
                              nextKeys.delete(containerKey);
                            } else {
                              nextKeys.add(containerKey);
                            }

                            return nextKeys;
                          });
                        }}
                      />
                    </label>
                  );
                },
              },
            ]}
            data={availableContainers}
            getRowId={getDeliveryMethodCreateContainerKey}
            gridClasses="grid-cols-[1fr_100px]"
          />
        )}
      </div>
    </form>
  );
};
